import React, { useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Container,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider
} from "@mui/material";
import {
  LinkedIn,
  Facebook,
  Instagram
} from "@mui/icons-material";
import {
  Visibility,
  VisibilityOff,
  PersonOutline,
  LockOutlined
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import { useLoginUserMutation } from "state/api";
import { useDispatch } from "react-redux";
import { login } from "state/index";
import logoImage from "assets/logo.png";
import one from "assets/1.jpg";
import three from "assets/3.jpg";
import four from "assets/4.jpg";
import five from "assets/5.jpg";
import six from "assets/6.jpg";
import seven from "assets/7.jpg";
import eight from "assets/8.jpg";
import nine from "assets/9.jpg";
import ten from "assets/10.jpg";
import eleven from "assets/11.jpg";
import twelve from "assets/12.jpg";
import thirteen from "assets/13.jpg";
import fourteen from "assets/14.jpg";
import fifteen from "assets/15.jpg";
import sixteen from "assets/16.jpg";
import seventeen from "assets/17.jpg";
import eighteen from "assets/18.jpg";
import nineteen from "assets/19.jpg";

const LoginPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({ EmpNo: "", password: "" });
  const [errors, setErrors] = useState({ EmpNo: false, password: false });
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [loginUser, { isLoading }] = useLoginUserMutation();

  const teamLogos = [
    one,
    three,
    four,
    five,
    six,
    seven,
    eight,
    nine,
    ten,
    eleven,
    twelve,
    thirteen,
    fourteen,
    fifteen,
    sixteen,
    seventeen,
    eighteen,
    nineteen
  ];
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: false });
    setErrorMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      EmpNo: formData.EmpNo.trim() === "",
      password: formData.password.trim() === "",
    };

    setErrors(newErrors);

    if (newErrors.EmpNo || newErrors.password) return;

    try {
      const response = await loginUser(formData).unwrap();

      if (response?.token && (response?.user.role[0] === 'B' || response?.user.role[0] === 'A')) {
        dispatch(login({
          token: response.token,
          EmpNo: response.user.id,
          MgCd: response.user.role,
          username: response.user.username
        }));

        const mgCdLength = response.user.role.length;
        if (mgCdLength === 7) {
          window.location.href = '/Mr';
        }
        else if (mgCdLength === 6) {
          window.location.href = '/Am';
        }
        else if (mgCdLength === 5 || mgCdLength === 4 || mgCdLength === 3) {
          window.location.href = '/Sm';
        }
        else if (mgCdLength === 2) {
          window.location.href = '/SrSm';
        }
        else if (response.user.role === 'A') {
          window.location.href = '/Admin';
        } else {
          setErrorMessage('Unknown role');
        }
      } else {
        setErrorMessage("Invalid credentials or missing token.");
      }
    } catch (err) {
      setErrorMessage(err?.data?.message || "Login Failed");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: theme.palette.background.default,
        p: 2
      }}
    >
      <Container component="main" maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              overflow: "hidden",
              borderRadius: 4,
              boxShadow: "0 15px 30px rgba(0,0,0,0.1)",
              backgroundColor: "white"
            }}
          >
            {/* Left side - Branding with Orbiting Team Logos */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                p: 10,
                background: "linear-gradient(135deg, rgba(177, 99, 241, 0.9) 0%, rgba(168, 85, 247, 0.9) 100%)",
                color: "white",
                textAlign: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Circular Orbit Container */}
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 0,
                }}
              >
                {/* Orbiting Logos */}
                {teamLogos.map((logo, index) => {
                  const angle = (index / teamLogos.length) * Math.PI * 2;
                  const radius = 150; // Orbit radius
                  const x = radius * Math.cos(angle);
                  const y = radius * Math.sin(angle);

                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{
                        opacity: 1,
                        scale: 1,
                        x: x,
                        y: y,
                        rotate: 360
                      }}
                      transition={{
                        duration: 0.8,
                        delay: index * 0.05,
                        rotate: {
                          duration: 20,
                          repeat: Infinity,
                          ease: "linear"
                        }
                      }}
                      style={{
                        position: "absolute",
                        zIndex: 1,
                      }}
                    >
                      <Box
                        component="img"
                        src={logo}
                        alt={`Team ${index + 1}`}
                        sx={{
                          width: 60,
                          height: 60,
                          objectFit: "cover",
                          borderRadius: "50%",
                          border: "2px solid rgba(255,255,255,0.5)",
                          boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                          transition: "transform 0.3s ease",
                          "&:hover": {
                            transform: "scale(1.2)",
                            boxShadow: "0 6px 15px rgba(0,0,0,0.3)",
                            zIndex: 2
                          }
                        }}
                      />
                    </motion.div>
                  );
                })}
              </Box>

              {/* Main Content - Centered */}
              <Box sx={{
                position: "relative",
                zIndex: 3,
                backdropFilter: "blur(5px)",
                backgroundColor: "rgba(0,0,0,0.1)",
                borderRadius: "50%",
                width: 200,
                height: 200,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                p: 2,
                border: "1px solid rgba(255,255,255,0.2)"
              }}>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    delay: 0.4,
                    duration: 0.6,
                    type: "spring",
                    stiffness: 260,
                    damping: 20
                  }}
                >
                  <Box
                    component="img"
                    src={logoImage}
                    alt="Company Logo"
                    sx={{
                      width: 120,
                      height: 120,
                      filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))"
                    }}
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                >
                  {/* <Typography variant="h4" sx={{ mt: 2, fontWeight: 700 }}>
        Welcome Back
      </Typography>
      <Typography variant="body1" sx={{ mt: 1, opacity: 0.9 }}>
        Sign in to access your dashboard
      </Typography> */}
                </motion.div>

                {/* Team Counter */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.2, duration: 0.5 }}
                  style={{ marginTop: theme.spacing(2) }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "8px 16px",
                      borderRadius: 20,
                      backgroundColor: "rgba(255,255,255,0.15)",
                      backdropFilter: "blur(4px)"
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {teamLogos.length} Teams
                    </Typography>
                  </Paper>
                </motion.div>
              </Box>
            </Box>

            {/* Right side - Login Form */}
            <Box
              sx={{
                flex: 1,
                p: 3,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                backgroundColor: theme.palette.background.paper
              }}
            >
              <Typography variant="h3" sx={{ mb: 1, fontWeight: 600, color: theme.palette.text.primary }}>
                Sign In
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Enter your credentials to continue
              </Typography>

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Employee ID"
                  name="EmpNo"
                  value={formData.EmpNo}
                  onChange={handleChange}
                  error={errors.EmpNo}
                  helperText={errors.EmpNo ? "Employee ID is required" : ""}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutline sx={{ color: theme.palette.text.secondary }} />
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.divider
                      }
                    }
                  }}
                  variant="outlined"
                  placeholder="Enter your employee ID"
                />

                <TextField
                  fullWidth
                  margin="normal"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  error={errors.password}
                  helperText={errors.password ? "Password is required" : ""}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: theme.palette.text.secondary }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ?
                            <VisibilityOff sx={{ color: theme.palette.text.secondary }} /> :
                            <Visibility sx={{ color: theme.palette.text.secondary }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.divider
                      }
                    }
                  }}
                  variant="outlined"
                  placeholder="Enter your password"
                />

                <Box sx={{ textAlign: "right", mt: 1 }}>
                  <Button
                    size="small"
                    sx={{
                      textTransform: 'none',
                      color: theme.palette.primary.main
                    }}
                  >
                    {/* Forgot password? */}
                  </Button>
                </Box>

                {errorMessage && (
                  <Typography
                    color="error"
                    variant="body2"
                    sx={{
                      mt: 2,
                      p: 1,
                      backgroundColor: theme.palette.error.light,
                      borderRadius: 1,
                      textAlign: 'center'
                    }}
                  >
                    {errorMessage}
                  </Typography>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  sx={{
                    mt: 4,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: 16,
                    letterSpacing: 0.5,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                    '&:hover': {
                      boxShadow: '0 8px 12px -2px rgba(0,0,0,0.1)',
                      transform: 'translateY(-1px)'
                    },
                    '&:active': {
                      transform: 'translateY(0)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Sign In"
                  )}
                </Button>

                <Divider sx={{ my: 3, color: theme.palette.text.secondary }}>Jagsonpal Social Media</Divider>


                <Box sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                  '& button': {
                    transition: 'all 0.2s ease'
                  }
                }}>
                  {[
                    {
                      icon: <LinkedIn fontSize="small" />,
                      url: 'https://in.linkedin.com/company/jagsonpalpharmaceuticals',
                      color: '#0A66C2',
                      label: 'LinkedIn'
                    },
                    {
                      icon: <Facebook fontSize="small" />,
                      url: 'https://www.facebook.com/Jagsonpalpharma',
                      color: '#1877F2',
                      label: 'Facebook'
                    },
                    {
                      icon: (
                        <Box sx={{
                          background: 'radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)',
                          borderRadius: '50%',
                          width: 24,
                          height: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Instagram sx={{ color: 'white', fontSize: 16 }} />
                        </Box>
                      ),
                      url: 'https://in.linkedin.com/company/jagsonpalpharmaceuticals',
                      label: 'Instagram'
                    }
                  ].map((social) => (
                    <IconButton
                      key={social.url}
                      component="a"
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="medium"
                      aria-label={social.label}
                      sx={{
                        width: 48,
                        height: 48,
                        backgroundColor: social.color || 'transparent',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      {social.icon}
                    </IconButton>
                  ))}
                </Box>
              </form>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default LoginPage;